import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { treatments } from "@/data/treatments";
import SEO from "@/components/SEO";

const stats = [
  { value: "20+", label: "Years in Medicine" },
  { value: "2,000+", label: "Treatments Performed" },
  { value: "500+", label: "Happy Clients" },
  { value: "100%", label: "Personalized Plans" }
];

const featuredSlugs = ["morpheus8", "hydrafacial-deluxe", "ipl-lumecca"];

export default function Home() {
  const featured = featuredSlugs.map(s => treatments.find(t => t.slug === s));

  return (
    <div data-testid="home-page" className="pt-20 md:pt-24">
      <SEO
        title="Natalia Medical Aesthetics | Morpheus8, HydraFacial & IPL Lumecca in Kirkland, WA"
        description="Explore Morpheus8, HydraFacial, and IPL Lumecca with Natalia Medical Aesthetics in Kirkland, WA. Book a personalized consultation."
        path="/"
      />
      {/* HERO */}
      <section data-testid="hero-section" className="relative overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 pt-14 md:pt-24 pb-20 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1 fade-up font-sans">
              <div
                data-testid="hero-overline"
                className="mb-8 text-[0.72rem] font-medium font-medium tracking-normal text-[#8A847C]"
              >
                Washington <span className="mx-2 text-[#C8C2BB]">|</span> Medical Aesthetics
              </div>
              <h1 className="font-sans text-[2.4rem] sm:text-5xl lg:text-[3.75rem] xl:text-[4.25rem] leading-[1.05] tracking-[-0.02em] text-[#1A1A1A] font-medium">
                Natalia Medical Aesthetics
              </h1>
              <p className="mt-6 md:mt-8 text-xl sm:text-2xl lg:text-[1.6rem] leading-[1.3] tracking-[-0.01em] text-[#1A1A1A] font-normal max-w-xl">
                Natural-looking results. Medical precision. Personalized care.
              </p>
              <p className="mt-6 md:mt-8 text-[1rem] md:text-[1.05rem] text-[#5C5C5C] leading-[1.65] max-w-md font-normal">
                Personalized medical aesthetic treatments designed to improve
                skin health while enhancing your natural beauty.
              </p>
              <div className="mt-10 md:mt-12 flex flex-wrap gap-3">
                <Link
                  to="/book"
                  data-testid="hero-book-btn"
                  className="inline-flex items-center justify-center bg-[#1A1A1A] text-white rounded-full px-8 py-4 text-[0.85rem] font-medium tracking-normal border border-[#1A1A1A] hover:bg-[#3A3A3A] hover:border-[#3A3A3A] transition-colors duration-300"
                >
                  Book Consultation
                </Link>
                <Link
                  to="/treatments"
                  data-testid="hero-treatments-btn"
                  className="inline-flex items-center justify-center bg-white text-[#1A1A1A] rounded-full px-8 py-4 text-[0.85rem] font-medium tracking-normal border border-[#D8D2CB] hover:border-[#1A1A1A] transition-colors duration-300"
                >
                  View Treatments
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6 order-1 lg:order-2 fade-in">
              <div className="rounded-[28px] overflow-hidden bg-[#F3EFEA] aspect-[4/5] max-w-[560px] ml-auto flex items-center justify-center">
                <img
                  src="https://customer-assets.emergentagent.com/job_aesthetic-premium-1/artifacts/1ncx2hla_4970279349600848870.jpg"
                  alt="Natalia — portrait"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section data-testid="stats-section" className="border-t border-[#E5E0DA]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-20 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="md:col-span-4">
              <div className="overline mb-5">Why Natalia</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Why clients choose <em className="italic text-[#8A847C]">Natalia</em>.
              </h2>
            </div>
            <div className="md:col-span-8 grid grid-cols-2 gap-6 md:gap-10">
              {stats.map((s, i) => (
                <div
                  key={s.label}
                  data-testid={`stat-card-${i}`}
                  className="bg-white rounded-[24px] border border-[#E5E0DA] p-8 md:p-10 min-h-[180px] md:min-h-[220px] flex flex-col justify-between transition-all duration-500 hover:shadow-[0_12px_48px_rgba(0,0,0,0.05)]"
                >
                  <div className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tight text-[#1A1A1A]">{s.value}</div>
                  <div className="text-xs md:text-sm text-[#5C5C5C] tracking-wide mt-4">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED TREATMENTS */}
      <section data-testid="featured-section" className="bg-[#F3EFEA]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14 md:mb-20">
            <div>
              <div className="overline mb-5">Featured Treatments</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-2xl">
                Signature protocols, <em className="italic text-[#8A847C]">honed over decades.</em>
              </h2>
            </div>
            <Link to="/treatments" data-testid="featured-view-all" className="editorial-link">
              View All Treatments <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
            {featured.map((t) => (
              <Link
                key={t.slug}
                to={`/treatments/${t.slug}`}
                data-testid={`featured-card-${t.slug}`}
                className="group bg-white rounded-[28px] border border-[#E5E0DA] overflow-hidden transition-all duration-500 hover:shadow-[0_20px_60px_rgba(0,0,0,0.06)]"
              >
                <div className="image-hover aspect-[4/3] bg-[#F3EFEA] overflow-hidden">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 md:p-10">
                  <h3 className="font-serif text-3xl md:text-4xl tracking-tight text-[#1A1A1A]">{t.name}</h3>
                  <p className="mt-4 text-[#5C5C5C] leading-relaxed">{t.tagline}</p>
                  <div className="mt-8 pt-6 border-t border-[#E5E0DA] flex items-center justify-between">
                    <span className="text-sm tracking-wide text-[#1A1A1A]">{t.price}</span>
                    <span className="text-[0.85rem] font-medium font-medium flex items-center gap-2 group-hover:gap-3 transition-all">
                      Learn More <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT PREVIEW */}
      <section data-testid="about-preview" className="border-t border-[#E5E0DA]">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <div className="lg:col-span-5">
              <div className="image-hover rounded-[28px] overflow-hidden aspect-[4/5] bg-[#F3EFEA]">
                <img
                  src="https://customer-assets.emergentagent.com/job_aesthetic-premium-1/artifacts/kxycemvo_IMG_3122.JPG"
                  alt="Natalia"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="lg:col-span-7 lg:pl-10">
              <div className="overline mb-6">About Natalia</div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                Two decades of medicine, <em className="italic text-[#8A847C]">devoted to the art of nuance.</em>
              </h2>
              <p className="mt-10 text-[#5C5C5C] leading-relaxed max-w-xl">
                Natalia's medical education and years of advanced professional
                training are dedicated to aesthetic care. Her approach is quiet
                and evidence-based: to enhance what is already there, never to
                reshape it.
              </p>
              <p className="mt-6 text-[#5C5C5C] leading-relaxed max-w-xl">
                Every treatment plan begins with a listening consultation and is built
                on a foundation of skin health, precision, and restraint.
              </p>
              <div className="mt-12">
                <Link to="/about" data-testid="about-preview-cta" className="editorial-link">
                  Read More <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="final-cta" className="bg-[#1A1A1A] text-white">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16 py-28 md:py-40 text-center">
          <div className="overline !text-[#8A847C] mb-8">Begin</div>
          <h2 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1.02] tracking-tight max-w-4xl mx-auto">
            Ready to begin your <em className="italic">skin journey?</em>
          </h2>
          <p className="mt-10 text-[#B8B2AB] max-w-xl mx-auto leading-relaxed">
            Book a private consultation with Natalia and let us design a plan
            that feels entirely, unmistakably yours.
          </p>
          <div className="mt-14">
            <Link
              to="/book"
              data-testid="final-cta-btn"
              className="inline-flex items-center gap-2 bg-white text-[#1A1A1A] rounded-full px-10 py-5 text-[0.85rem] font-medium font-medium hover:bg-[#F3EFEA] transition-colors"
            >
              Book Consultation <ArrowUpRight strokeWidth={1.5} className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
